import { isMobileBrowser, showErrorAlertDialog } from '@/features/common/helpers';
import { Vue, Component, Watch } from 'vue-property-decorator';
import { UserPreferences } from '@/features/settings/types';
import PrintLabel from '@/features/shipment/components/print/PrintLabel.vue';
import DoneToDashboardFooter from '@/features/common/components/DoneToDashboardFooter.vue';
import { getShipmentPrintData, getShipmentPrintLabelPdf, getShipmentPrintLabelSrc } from '../../services/shipment.services';
import { downloadPdf, printPdfDocument } from '../../helpers/printPdf';
import { modifyOrReturnShipment } from '../../helpers/createShipment';
import { ShipmentPrintData } from '../../types';

@Component({
  components: {
    PrintLabel,
    DoneToDashboardFooter,
  },
})
export default class PrintMixin extends Vue {
  public isLoading = false;

  public isPrintDataLoaded = false;

  public currentLabelSize = this.userPreferences.printSettings.labelSize || 'Standard';

  public pdfSrc = '';

  public isEnlarge = false;

  public showEmailDocs = false;

  public shipmentPrintData: ShipmentPrintData | null = null;

  public isQRCodeMode = false;

  get isInitialDataLoaded(): number {
    return this.$store.getters.getIsInitialDataLoaded && !!this.shipmentPrintData;
  }

  get secureshipTrackingNo(): string | null {
    return this.$route.query.id as string;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get isGlobalLoading(): boolean {
    return this.$store.state.loading;
  }

  @Watch('secureshipTrackingNo', { immediate: true })
  private getData(newValue) {
    if (!newValue) return;
    this.loadShipmentPrintData();
  }

  public async getPrintLabelSrcAndPrint() {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    this.isLoading = true;
    const pdfSrc = await getShipmentPrintLabelSrc(
      this.secureshipTrackingNo, this.currentLabelSize, this.isQRCodeMode,
    );

    if (!pdfSrc) {
      showErrorAlertDialog(this.$t('shipment.alertDialog.labelIsNotAvailable'));
      this.isLoading = false;
      return;
    }

    this.pdfSrc = pdfSrc;

    if (isMobileBrowser()) {
      await this.downloadPdfDocument(this.isQRCodeMode);
    } else {
      await printPdfDocument(pdfSrc);
    }

    this.isLoading = false;
  }

  public async downloadPdfDocument(getQrCode = false) {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    const pdfResponse = await getShipmentPrintLabelPdf(
      this.secureshipTrackingNo, this.currentLabelSize, getQrCode,
    );

    if (pdfResponse) await downloadPdf(pdfResponse as string, 'label.pdf');
  }

  public modifyOrReturnShipmentWithForce(opType: 'modify' | 'return') {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    modifyOrReturnShipment(
      this.secureshipTrackingNo,
      opType,
      true,
    );
  }

  public async loadShipmentPrintData(): Promise<void> {
    if (!this.secureshipTrackingNo) {
      this.showTrackingNumberInvalidWarning();
      return;
    }

    this.$store.commit('setLoading', true);
    this.shipmentPrintData = await getShipmentPrintData(
      this.secureshipTrackingNo,
    );
    this.$store.commit('setLoading', false);

    const { isLabelFree, isBoxFree } = this.shipmentPrintData || {};
    this.isQRCodeMode = !!(isLabelFree || isBoxFree);

    if (this.redirectToReturnShipmentIfLabelFree(this.shipmentPrintData)) return;

    if (this.shipmentPrintData) {
      this.isPrintDataLoaded = true;
      return;
    }

    showErrorAlertDialog(this.$t('shipment.alertDialog.shipmentDataNotAvailable'));
    this.isLoading = false;
  }

  public showEmailDocsDialog() {
    this.showEmailDocs = true;
  }

  public showTrackingNumberInvalidWarning() {
    showErrorAlertDialog(this.$t('shipment.alertDialog.trackingNumberInvalid'));
  }

  private redirectToReturnShipmentIfLabelFree(printData?: ShipmentPrintData | null): boolean {
    if (!printData) return false;
    if (this.$route.name === 'shipment-return') return false;

    const { isBoxFree, isLabelFree, isReturnShipment } = printData;

    const isLabelFreeShipment = isBoxFree || isLabelFree || isReturnShipment;

    if (!isLabelFreeShipment) return false;

    this.$router.replace({
      name: 'shipment-return',
      query: { id: this.secureshipTrackingNo },
    });

    return true;
  }
}
